import React, { useEffect, useState } from "react";
import { BookOpen, Check, ChevronRight, FileCode2, Play, Search, ShieldCheck, Sparkles, type LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import type { StrategyRdCoachActivity, StrategyRdCoachTurnTrace } from "@/types/strategyRdCoach";

/** Brand gradient (`--gradient-primary`) running through the live step's label. */
const SHIMMER_CSS = `
@keyframes strategy-rd-coach-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
.strategy-rd-coach-shimmer {
  background-image: linear-gradient(90deg, var(--brand-tropical-surf) 0%, var(--brand-tropical-surf) 30%,
    var(--brand-neon-aqua) 50%, var(--brand-tropical-surf) 70%, var(--brand-tropical-surf) 100%);
  background-size: 200% 100%;
  -webkit-background-clip: text; background-clip: text; color: transparent;
  animation: strategy-rd-coach-shimmer 2.4s linear infinite;
}
@media (prefers-reduced-motion: reduce) { .strategy-rd-coach-shimmer { animation: none; color: var(--brand-tropical-surf); } }
`;

function seconds(n: number): string {
  if (n < 60) return `${Math.max(0, Math.round(n))}s`;
  const m = Math.floor(n / 60);
  return `${m}m ${Math.round(n - m * 60)}s`;
}

/** An icon for what the step is doing, from TES's own label ("Reading your Lab", "Running the backtest …"). */
function stepIcon(label: string): LucideIcon | null {
  if (/read/i.test(label)) return BookOpen;
  if (/think|plan/i.test(label)) return Sparkles;
  if (/check/i.test(label)) return ShieldCheck;
  if (/backtest|running|search|sweep|robust/i.test(label)) return Play;
  if (/look(ing)? up|concept/i.test(label)) return Search;
  if (/writ|compil|recipe|rule/i.test(label)) return FileCode2;
  return null;
}

/** A finished "Thinking" step reads "Thought for 4s"; every other label stays as TES sent it. */
function doneLabel(label: string, secs?: number | null): { lead: string | null; label: string } {
  if (/^thinking$/i.test(label.trim()) && secs != null && secs >= 1) return { lead: `Thought for ${seconds(secs)}`, label: "" };
  return { lead: null, label };
}

/** Timeline node: the step's icon in a quiet ring when done, white on the brand gradient while live. */
function StepNode({ live, logoSrc, label }: { live?: boolean; logoSrc?: string; label: string }) {
  const Icon = stepIcon(label);
  if (live) {
    return (
      <span className="relative z-[1] flex size-6 shrink-0 items-center justify-center rounded-full bg-[image:var(--gradient-primary)] shadow-[0_0_10px_rgba(56,187,209,0.45)]">
        {Icon ? (
          <Icon className="size-3.5 text-white" strokeWidth={2.25} aria-hidden />
        ) : logoSrc ? (
          <img
            src={logoSrc}
            alt=""
            width={12}
            height={12}
            className="size-3 animate-spin object-contain brightness-0 invert [animation-duration:1.6s] motion-reduce:animate-none"
            draggable={false}
          />
        ) : null}
      </span>
    );
  }
  return (
    <span className="relative z-[1] flex size-6 shrink-0 items-center justify-center rounded-full !border !border-solid !border-border bg-background">
      {Icon ? (
        <Icon className="size-3.5 text-gray-500" aria-hidden />
      ) : (
        <Check className="size-3 text-[color:var(--brand-tropical-surf)]" aria-hidden />
      )}
    </span>
  );
}

/** Finished step: node, label, time — on the timeline's rail. */
function StepRow({ label, secs }: { label: string; secs?: number | null }) {
  const shown = doneLabel(label, secs);
  return (
    <li className="relative flex items-center gap-3 !py-1 font-[family-name:var(--font-primary)] text-[13px] leading-5 text-gray-500 dark:text-muted-foreground">
      <StepNode label={label} />
      <span className="min-w-0 flex-1 truncate">
        {shown.lead ? <span className="text-gray-400">{shown.lead}</span> : null}
        {shown.label}
      </span>
      {secs != null && secs >= 1 && !shown.lead ? (
        <span className="shrink-0 font-[family-name:var(--font-secondary)] text-[11px] tabular-nums text-gray-400">
          {seconds(secs)}
        </span>
      ) : null}
    </li>
  );
}

/** Steps stacked on a vertical rail that runs through the node centres. */
function StepList({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <ol
      className={cn(
        "relative !m-0 flex flex-col gap-1 !p-0 !list-none",
        "before:absolute before:bottom-3 before:left-[11px] before:top-3 before:w-px before:bg-border",
        className
      )}
    >
      {children}
    </ol>
  );
}

/** Live: what TES is doing right now, step by step, with a running timer. */
export function StrategyRdCoachLiveActivity({
  activity,
  logoSrc,
}: {
  activity: StrategyRdCoachActivity | null;
  logoSrc: string;
}): JSX.Element {
  const [, setTick] = useState(0);
  const [mountedAt] = useState(() => Date.now());
  const [readAt, setReadAt] = useState(() => Date.now());
  useEffect(() => setReadAt(Date.now()), [activity]);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 250);
    return () => window.clearInterval(id);
  }, []);

  const steps = activity?.steps ?? [];
  // Server clock now = its last reading plus the time since we read it.
  const serverNow = activity?.now != null ? activity.now + (Date.now() - readAt) / 1000 : null;
  const done = steps.slice(0, -1);
  const current = steps[steps.length - 1];
  const currentLabel = current?.label ?? "Thinking";
  const currentSecs =
    current && serverNow != null ? serverNow - current.at : (Date.now() - mountedAt) / 1000;

  return (
    <div className="flex flex-col gap-1.5" role="status" aria-live="polite" data-strategy-rd-coach-activity="">
      <style>{SHIMMER_CSS}</style>
      {done.length > 0 ? (
        <StepList className="before:-bottom-2">
          {done.map((s, i) => (
            <StepRow key={`${s.label}-${i}`} label={s.label} secs={steps[i + 1].at - s.at} />
          ))}
        </StepList>
      ) : null}
      <div
        className={cn(
          // Quiet live row: a light brand outline, no heavy fill (the shimmer carries the motion).
          "relative flex items-center gap-3 rounded-[8px] !py-1.5 !pl-1 !pr-3",
          "!border !border-solid !border-[color:var(--brand-tropical-surf)]/20 bg-[color:var(--brand-tropical-surf)]/[0.04]"
        )}
      >
        <StepNode live logoSrc={logoSrc} label={currentLabel} />
        <span className="strategy-rd-coach-shimmer min-w-0 flex-1 truncate font-[family-name:var(--font-primary)] text-[13px] font-semibold">
          {currentLabel}
        </span>
        <span className="shrink-0 font-[family-name:var(--font-secondary)] text-[11px] tabular-nums text-gray-400">
          {seconds(currentSecs)}
        </span>
      </div>
    </div>
  );
}

/** After the reply: "Worked for 59s · 8 steps · 1 backtest run", expandable to the steps. */
export function StrategyRdCoachTurnTraceView({ trace }: { trace: StrategyRdCoachTurnTrace }): JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (!trace.steps.length) return null;
  const backtests = trace.steps.filter((s) => /backtest/i.test(s.label)).length;
  return (
    <div className="flex flex-col" data-strategy-rd-coach-trace="">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="group flex w-fit items-center gap-1.5 !rounded-[6px] !px-0 !py-0.5 font-[family-name:var(--font-primary)] !text-xs !text-gray-500 transition-colors hover:!text-[color:var(--brand-tropical-surf)] dark:!text-muted-foreground"
      >
        <ChevronRight className={cn("size-3.5 transition-transform", open && "rotate-90")} aria-hidden />
        <span>Worked for {seconds(trace.seconds)}</span>
        <span className="text-gray-400">
          · {trace.steps.length} step{trace.steps.length === 1 ? "" : "s"}
          {backtests > 0 ? ` · ${backtests} backtest run${backtests === 1 ? "" : "s"}` : ""}
        </span>
      </button>
      {open ? (
        <StepList className="mt-2.5 pl-0.5">
          {trace.steps.map((s, i) => (
            <StepRow key={`${s.label}-${i}`} label={s.label} secs={s.seconds} />
          ))}
        </StepList>
      ) : null}
    </div>
  );
}
